import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTeamId } from "./use-team-id";
import { useToast } from "./use-toast";
import type { Database } from "@/integrations/supabase/types";

type MessageRow = Database["public"]["Tables"]["messages"]["Row"];

/**
 * Hook Realtime : écoute les INSERT sur la table `messages` et :
 * 1. Met à jour le cache React Query de la timeline (opportunity-messages)
 * 2. Invalide le pipeline + dashboard (une réponse = action chaude)
 * 3. Affiche un toast minimaliste pour les messages entrants
 *
 * À monter UNE SEULE FOIS dans le layout principal (AppLayout).
 */
export function useWahaRealtime() {
  const queryClient = useQueryClient();
  const { teamId } = useTeamId();
  const { toast } = useToast();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const sessionChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!teamId) return;

    const channel = supabase
      .channel("messages-realtime")
      .on<MessageRow>(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "messages",
        },
        async (payload) => {
          const msg = payload.new;

          // ── Enrichir le cache de la timeline ──────────────────────
          const attachments = (msg.attachments_json ?? {}) as Record<
            string,
            unknown
          >;
          const opportunityId = attachments?.opportunity_id as string | null;

          if (opportunityId) {
            queryClient.setQueryData<MessageRow[]>(
              ["opportunity-messages", opportunityId],
              (old) => {
                if (!old) return [msg];
                // Dédoublonner (idempotence côté UI aussi)
                if (old.some((m) => m.id === msg.id)) return old;
                // Ajouter en fin (ordre chronologique ASC)
                return [...old, msg];
              }
            );
          }

          // ── Invalider le cache de la page Messages ────────────
          queryClient.invalidateQueries({
            queryKey: ["conversation-messages", msg.conversation_id],
          });
          queryClient.invalidateQueries({
            queryKey: ["unified-messages"],
          });
          queryClient.invalidateQueries({
            queryKey: ["conversations"],
          });

          // ── Toast uniquement pour les messages entrants (hors contacts personnels) ──
          if (msg.direction === "in") {
            const { data: convCheck } = await supabase
              .from("conversations")
              .select("immo_contacts!conversations_contact_id_fkey(is_personal)")
              .eq("id", msg.conversation_id)
              .single();
            const isPersonal = (convCheck?.immo_contacts as { is_personal?: boolean } | null)?.is_personal;
            if (isPersonal) return;

            const channel = (attachments?.channel as string) ?? "whatsapp";
            const isEmail = channel === "email";

            const phone = (attachments?.phone as string) ?? "inconnu";
            const senderLabel = isEmail
              ? ((attachments?.from as string) ?? "email")
              : phone.startsWith("33") && phone.length === 11
                ? "0" + phone.slice(2)
                : phone;

            toast({
              title: isEmail ? "Nouveau email reçu" : "Nouveau message WhatsApp",
              description: `${senderLabel} — ${(msg.content ?? "").slice(0, 80)}`,
            });

            // Marquer stale sans refetch immediat (sera rafraichi a la navigation)
            queryClient.invalidateQueries({
              queryKey: ["pipeline-opportunities"],
              refetchType: "none",
            });
            queryClient.invalidateQueries({
              queryKey: ["dashboard-stats"],
              refetchType: "none",
            });
            queryClient.invalidateQueries({
              queryKey: ["dashboard-hot-leads"],
              refetchType: "none",
            });
            queryClient.invalidateQueries({
              queryKey: ["dashboard-ai-alerts"],
              refetchType: "none",
            });
            queryClient.invalidateQueries({
              queryKey: ["dashboard-followups"],
              refetchType: "none",
            });

            // Marquer l'opportunité comme ayant un message non lu
            if (opportunityId) {
              queryClient.setQueryData<Set<string>>(
                ["inbound-unread"],
                (old) => {
                  const set = new Set(old ?? []);
                  set.add(opportunityId);
                  return set;
                }
              );
            }

            // Marquer la conversation comme non lue
            queryClient.setQueryData<Set<string>>(
              ["inbound-unread-conversations"],
              (old) => {
                const set = new Set(old ?? []);
                set.add(msg.conversation_id);
                return set;
              }
            );
          }
        }
      )
      .on<MessageRow>(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "messages",
        },
        (payload) => {
          const msg = payload.new;

          // Mettre à jour le cache conversation-messages avec le nouveau statut
          queryClient.setQueryData<MessageRow[]>(
            ["conversation-messages", msg.conversation_id],
            (old) => {
              if (!old) return old;
              return old.map((m) => (m.id === msg.id ? msg : m));
            }
          );

          // Invalider le cache unified-messages (statut mis à jour)
          queryClient.invalidateQueries({
            queryKey: ["unified-messages"],
          });

          // Mettre à jour le cache opportunity-messages si pertinent
          const attachments = (msg.attachments_json ?? {}) as Record<string, unknown>;
          const opportunityId = attachments?.opportunity_id as string | null;
          if (opportunityId) {
            queryClient.setQueryData<MessageRow[]>(
              ["opportunity-messages", opportunityId],
              (old) => {
                if (!old) return old;
                return old.map((m) => (m.id === msg.id ? msg : m));
              }
            );
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [teamId, queryClient, toast]);

  // ── Realtime sur waha_sessions : push instantane du statut WhatsApp ──
  useEffect(() => {
    if (!teamId) return;

    const sessionChannel = supabase
      .channel("waha-sessions-realtime")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "waha_sessions",
        },
        (payload) => {
          const newRow = payload.new as Record<string, unknown>;
          console.debug("[waha-realtime] waha_sessions updated:", newRow.status);
          queryClient.invalidateQueries({ queryKey: ["waha-session"] });
        }
      )
      .subscribe();

    sessionChannelRef.current = sessionChannel;

    return () => {
      sessionChannel.unsubscribe();
      sessionChannelRef.current = null;
    };
  }, [teamId, queryClient]);

  // ── Realtime sur briefing_actions : badge sidebar + messagerie ──
  useEffect(() => {
    if (!teamId) return;

    const briefingChannel = supabase
      .channel("briefing-actions-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "briefing_actions",
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["briefing-action-count"] });
          queryClient.invalidateQueries({ queryKey: ["briefing-actions"] });
        }
      )
      .subscribe();

    return () => {
      briefingChannel.unsubscribe();
    };
  }, [teamId, queryClient]);
}

/**
 * Lit l'ensemble des opportunity_ids avec messages non lus.
 * Utilisé par PipelineCard pour afficher le badge rouge.
 */
export function useInboundUnread(): Set<string> {
  const queryClient = useQueryClient();
  return queryClient.getQueryData<Set<string>>(["inbound-unread"]) ?? new Set();
}

/**
 * Marque une opportunité comme lue (retire le badge rouge).
 * Appeler quand l'agent ouvre le détail / onglet Contacter.
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return (id: string) => {
    // Marquer l'opportunité comme lue
    queryClient.setQueryData<Set<string>>(["inbound-unread"], (old) => {
      if (!old) return new Set();
      const set = new Set(old);
      set.delete(id);
      return set;
    });
    // Marquer aussi la conversation comme lue (même ID peut être l'un ou l'autre)
    queryClient.setQueryData<Set<string>>(["inbound-unread-conversations"], (old) => {
      if (!old) return new Set();
      const set = new Set(old);
      set.delete(id);
      return set;
    });
  };
}

/**
 * Lit l'ensemble des conversation_ids avec messages non lus.
 * Utilisé par la page Messages pour afficher les badges.
 */
export function useInboundUnreadConversations(): Set<string> {
  const queryClient = useQueryClient();
  return queryClient.getQueryData<Set<string>>(["inbound-unread-conversations"]) ?? new Set();
}
